import { createSSRApp, markRaw, type App as VueApp } from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import "vue-toastification/dist/index.css";
//import firebaseApp from "@/utils/firebaseService";
import App from "./App.vue";
import router from "./router";
import "./styles/index.css";
//import { VueFire } from "vuefire";
import "overlayscrollbars/overlayscrollbars.css";
import { useNotificationStore } from "./new_design/notifications/application/stores/notificationStore";

const FloatingVue = async (app: VueApp) => {
  const { default: FloatingVue } = await import("floating-vue");
  app.use(FloatingVue);
};

const DragAndDropVue = async (app: VueApp) => {
  const { default: DragAndDrop } = await import("vue3-drag-drop");
  app.use(DragAndDrop);
};

const Toast = async (app: VueApp) => {
  const { default: Toast, POSITION, TYPE } = await import("vue-toastification");
  app.use(Toast, {
    transition: "Vue-Toastification__bounce",
    maxToasts: 20,
    newestOnTop: true,
    position: POSITION.TOP_RIGHT,
    timeout: 1000,
    closeOnClick: true,
    pauseOnFocusLoss: true,
    pauseOnHover: true,
    draggable: false,
    draggablePercent: 0.6,
    showCloseButtonOnHover: false,
    hideProgressBar: true,
    closeButton: false,
    icon: false,
    rtl: false,
    clearTimeout: 500,
  });
};

const VueEasyLightbox = async (app: VueApp) => {
  const { default: VueEasyLightbox } = await import("vue-easy-lightbox");
  app.use(VueEasyLightbox);
};

const VueQueryPlugin = async (app: VueApp) => {
  const { VueQueryPlugin } = await import("@tanstack/vue-query");

  VueQueryPlugin.install(app, {
    queryClientConfig: {
      defaultOptions: {
        queries: {
          cacheTime: 1000, //1 segundo en cache
        },
      },
    },
  });
};

const app = createSSRApp(App);
if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/firebase-messaging-sw.js") // 👈 Make sure the path is correct
    .then((registration) => {})
    .catch((err) => {
      console.error("Error registering Service Worker:", err);
    });
}
Toast(app);
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
pinia.use(({ store }) => {
  store.router = markRaw(router);
});

//app.use(VueFire, { firebaseApp, modules: [] });
app.use(pinia);
app.use(router);

VueQueryPlugin(app);

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "NEW_NOTIFICATION") {
      const payload = event.data.payload;

      // Update notification store
      const notificationStore = useNotificationStore();
      notificationStore.addNotification(payload);
    }
  });
}

FloatingVue(app);

VueEasyLightbox(app);
DragAndDropVue(app);

const initSentry = async () => {
  const Sentry = await import("@sentry/vue");

  const stage = import.meta.env.VITE_STAGE || import.meta.env.MODE;
  const isDevelopment = stage === "dev" || stage === "development";

  const sentryConfig = {
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN || "",
    environment: stage,
    sendDefaultPii: true, // Capture IP addresses and user info
    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: [
          "localhost",
          new RegExp(`^${import.meta.env.VITE_API_BASEURL}`),
        ],
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
      Sentry.replayIntegration(),
    ],
    // Environment-specific sample rates
    tracesSampleRate: isDevelopment ? 1.0 : 0.5, // 100% in dev, 50% in prod
    replaysSessionSampleRate: isDevelopment ? 0.5 : 0.1, // Higher in dev for testing
    replaysOnErrorSampleRate: 1.0, // Always capture errors

    // Enable debug mode in development
    //debug: isDevelopment,

    // Optionally reduce noise in development
    beforeSend(event, hint) {
      // In development, you can add custom filtering logic here
      if (isDevelopment) {
        //console.log("Sentry Event:", event);
      }
      return event;
    },
  };
  Sentry.init(sentryConfig);
};

// Initialize Sentry in all modes - configuration adjusts based on MODE
(async () => {
  await initSentry();
})();

app.mount("#app");
app.config.globalProperties.window = window;
